// ── Enums / string unions ──────────────────────────────────────────────────────

export type BankInterestWorkflowStatus =
  | 'Draft'
  | 'Submitted'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected'
  | 'Posted'
  | 'Cancelled';

export type BankInterestPostingStatus = 'NotPosted' | 'Posted' | 'Cancelled';

export type BankInterestType = 'Income' | 'Expense';

// ── Status display helpers ────────────────────────────────────────────────────

export const BNK_INTEREST_WORKFLOW_STATUS_LABELS: Record<BankInterestWorkflowStatus, string> = {
  Draft:          'Draft',
  Submitted:      'Submitted',
  PendingApproval:'Pending Approval',
  Approved:       'Approved',
  Rejected:       'Rejected',
  Posted:         'Posted',
  Cancelled:      'Cancelled',
};

export const BNK_INTEREST_WORKFLOW_STATUS_COLORS: Record<BankInterestWorkflowStatus, string> = {
  Draft:          'bg-gray-100 text-gray-700',
  Submitted:      'bg-blue-100 text-blue-700',
  PendingApproval:'bg-yellow-100 text-yellow-700',
  Approved:       'bg-green-100 text-green-700',
  Rejected:       'bg-red-100 text-red-700',
  Posted:         'bg-purple-100 text-purple-700',
  Cancelled:      'bg-gray-200 text-gray-500',
};

// ── Detail DTO ────────────────────────────────────────────────────────────────

export interface BankInterestDto {
  id:                         string;
  companyId:                  string;
  locationId?:                string | null;
  costCenterId?:              string | null;
  financialYearCode:          string;
  financialPeriodCode:        string;
  interestNumber:             string;
  interestDate:               string;
  postingDate:                string;
  bankAccountId:              string;
  bankAccountCode:            string;
  bankAccountName:            string;
  bankGLAccountId?:           string | null;
  interestType:               BankInterestType;
  incomeExpenseAccountId?:    string | null;
  incomeExpenseAccountCode?:  string | null;
  incomeExpenseAccountName?:  string | null;
  whtAccountId?:              string | null;
  whtAccountCode?:            string | null;
  whtAccountName?:            string | null;
  currencyCode:               string;
  exchangeRate:               number;
  amount:                     number;
  whtRate:                    number;
  whtAmount:                  number;
  netAmount:                  number;
  baseAmount:                 number;
  reference?:                 string | null;
  narration?:                 string | null;
  workflowStatus:             BankInterestWorkflowStatus;
  postingStatus:              BankInterestPostingStatus;
  submittedBy?:               string | null;
  submittedDate?:             string | null;
  approvedBy?:                string | null;
  approvedDate?:              string | null;
  rejectedBy?:                string | null;
  rejectedDate?:              string | null;
  rejectionReason?:           string | null;
  postedBy?:                  string | null;
  postedDate?:                string | null;
  cancelledBy?:               string | null;
  cancelledDate?:             string | null;
  cancellationReason?:        string | null;
  isReversal:                 boolean;
  originalInterestId?:        string | null;
  reversalInterestId?:        string | null;
  sourceGlJournalId?:         string | null;
  createdDate:                string;
}

// ── List row DTO ──────────────────────────────────────────────────────────────

export interface BankInterestListDto {
  id:              string;
  interestNumber:  string;
  interestDate:    string;
  postingDate:     string;
  bankAccountCode: string;
  bankAccountName: string;
  interestType:    BankInterestType;
  currencyCode:    string;
  amount:          number;
  whtAmount:       number;
  netAmount:       number;
  baseAmount:      number;
  workflowStatus:  BankInterestWorkflowStatus;
  postingStatus:   BankInterestPostingStatus;
  reference?:      string | null;
  isReversal:      boolean;
  createdDate:     string;
}

// ── Filter DTO ────────────────────────────────────────────────────────────────

export interface BankInterestFilterDto {
  companyId?:          string;
  locationId?:         string;
  costCenterId?:       string;
  financialYearCode?:  string;
  bankAccountId?:      string;
  interestType?:       string;
  workflowStatus?:     string;
  postingStatus?:      string;
  dateFrom?:           string;
  dateTo?:             string;
  search?:             string;
  pageNumber?:         number;
  pageSize?:           number;
  sortBy?:             string;
  sortDescending?:     boolean;
}

// ── Create / Update requests ──────────────────────────────────────────────────

export interface CreateBankInterestRequest {
  companyId:               string;
  locationId?:             string | null;
  costCenterId?:           string | null;
  financialYearCode:       string;
  financialPeriodCode:     string;
  interestDate:            string;
  postingDate:             string;
  bankAccountId:           string;
  interestType:            BankInterestType;
  incomeExpenseAccountId?: string | null;
  whtAccountId?:           string | null;
  currencyCode:            string;
  exchangeRate:            number;
  amount:                  number;
  whtRate:                 number;
  reference?:              string | null;
  narration?:              string | null;
}

export interface UpdateBankInterestRequest {
  interestDate:               string;
  postingDate:                string;
  bankAccountId:              string;
  bankAccountCode:            string;
  bankAccountName:            string;
  bankGLAccountId?:           string | null;
  interestType:               BankInterestType;
  incomeExpenseAccountId?:    string | null;
  incomeExpenseAccountCode?:  string | null;
  incomeExpenseAccountName?:  string | null;
  whtAccountId?:              string | null;
  whtAccountCode?:            string | null;
  whtAccountName?:            string | null;
  currencyCode:               string;
  exchangeRate:               number;
  amount:                     number;
  whtRate:                    number;
  reference?:                 string | null;
  narration?:                 string | null;
}
